/**
 * ErrorReporter.js
 * Talkroute
 *
 * Created by Vel Genov on 2/1/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React from "react";
import { connect } from "react-redux";
import { Label, Button, Icon } from "semantic-ui-react";

import { fetchCallHistoryUpdates } from "../actions/callHistoryActions";
import { fetchConversations } from "../actions/conversationActions";
import { fetchVoicemails } from "../actions/voicemailActions";
import { fetchPhoneNumbers } from "../actions/preferencesActions";
import { clearErrors } from "../actions/errorActions";
import { fetchConnectionData } from "../actions/webRTCConnectionActions";
import uiString from "../constants/uiString";
import apiConstants from "../constants/apiConstants";
import CloseIcon from '@material-ui/icons/Close';
import { IconButton } from "@material-ui/core";

const errorReporterStyle = {
  position: "absolute" as any,
  zIndex: 4,
  width: "100%",
  textAlign: "center" as any,
  paddingLeft: "20px",
  paddingRight: "20px",
  paddingTop: "60px",
};

interface IErrorReporterProps {
  callHistoryError: Error;
  conversationsError: Error;
  preferencesError: Error;
  voicemailsError: Error;
  webRTCConnectionError: Error;
  fetchCallHistoryUpdates: (background?: boolean) => any;
  fetchConversations: (
    since?: any,
    offset?: any,
    showLoading?: boolean | undefined,
    clearDraft?: boolean | undefined
  ) => any;
  fetchVoicemails: (background?: boolean) => any;
  fetchPhoneNumbers: () => any;
  clearErrors: () => any;
  fetchConnectionData: () => any;
}

/**
 * Process all incoming errors and show up only if there is an error present
 */
class ErrorReporter extends React.Component<IErrorReporterProps, any> {
  /**
   * A request to try and fetch callHistory
   */
  retryCallHistory = () => {
    const { fetchCallHistoryUpdates: fetchCallHistoryLocal } = this.props;
    fetchCallHistoryLocal();
  };

  /**
   * A request to try and fetch conversations
   */
  retryConversations = () => {
    const { fetchConversations: fetchConversationsLocal } = this.props;
    fetchConversationsLocal();
  };

  /**
   * A request to try and fetch voicemails
   */
  retryVoicemails = () => {
    const { fetchVoicemails: fetchVoicemailsLocal } = this.props;
    fetchVoicemailsLocal();
  };

  /**
   * A request to try and fetch the user's phone numbers
   */
  retryUserPhoneNumbers = () => {
    const { fetchPhoneNumbers: fetchPhoneNumbersLocal } = this.props;
    fetchPhoneNumbersLocal();
  };

  /**
   * A request to try and fetch the WebRTC connection data
   */
  retryWebRTCConnectionData = () => {
    const { fetchConnectionData: fetchConnectionDataLocal } = this.props;
    fetchConnectionDataLocal();
  };

  /**
   * Request to clear all errors
   */
  clearAllErrors = () => {
    const { clearErrors: clearErrorsLocal } = this.props;
    clearErrorsLocal();
  };

  /**
   * Go through all error messages and see if any exist
   *
   * Note, this function will only pick the last error message to display!
   *
   * @returns {*} an error message and error function to call back
   */
  getErrorMessageAndAction = () => {
    const {
      callHistoryError,
      conversationsError,
      preferencesError,
      voicemailsError,
      webRTCConnectionError,
    } = this.props;
    let errorMessage: string | null = null;
    let retryFunction: (() => any) | null = null;

    if (callHistoryError) {
      errorMessage = callHistoryError.message;
      retryFunction = this.retryCallHistory;
    }

    if (conversationsError) {
      errorMessage = conversationsError.message;
      retryFunction = this.retryConversations;
    }

    if (preferencesError) {
      errorMessage = preferencesError.message;
      retryFunction = this.retryUserPhoneNumbers;
    }

    if (voicemailsError) {
      errorMessage = this.generateVoicemailErrorMessage(voicemailsError);
      retryFunction = this.retryVoicemails;
    }

    if (webRTCConnectionError) {
      errorMessage = webRTCConnectionError.message;
      retryFunction = this.retryWebRTCConnectionData;
    }

    return { errorMessage, retryFunction };
  };

  /**
   * Parses the error message for voicemail
   * Substitutes for a prettier message in select cases
   *
   * @param error the error used to generate a message
   * @returns a string containing the parsed Voicemail error message
   */
  generateVoicemailErrorMessage = (error) => {
    const { message } = error;

    switch (message) {
      case apiConstants().errors.VOICEMAIL_FILE_NOT_FOUND:
        return uiString.VOICEMAIL_FILE_NOT_FOUND_ERROR;
      default:
        return message;
    }
  };

  /**
   * Show the error reporter if the error is non null
   */
  showErrorBody = (error, callback) => {
    const body = error ? (
      <div
        style={{
          ...errorReporterStyle,
        }}
      >
        <Label style={{ width: "100%" }}>
          {error}
          <Button
            basic
            icon
            compact
            style={{ boxShadow: "none", margin: "5px" }}
            onClick={callback}
          >
            {uiString.RETRY}
          </Button>

          <IconButton
            style={{ margin: "5px", float: "right" }}
            onClick={this.clearAllErrors}
          >
            <CloseIcon />
          </IconButton>
        </Label>
      </div>
    ) : null;

    return body;
  };

  render() {
    const { errorMessage, retryFunction } = this.getErrorMessageAndAction();
    return this.showErrorBody(errorMessage, retryFunction);
  }
}

// Redux data binding
const mapStateToProps = (state) => ({
  callHistoryError: state.callHistory.error,
  conversationsError: state.conversations.error,
  voicemailsError: state.voicemails.error,
  preferencesError: state.preferences.error,
  webRTCConnectionError: state.webRTCConnection.error,
});

export default connect(mapStateToProps, {
  fetchCallHistoryUpdates,
  fetchConversations,
  fetchPhoneNumbers,
  fetchVoicemails,
  clearErrors,
  fetchConnectionData,
})(ErrorReporter);
